import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { Warning as WarningIcon, Send as SendIcon } from '@mui/icons-material';
import axios from 'axios';
import MetaTags from '../components/MetaTags';
import { getApiUrl } from '../utils/apiUrl';

const AccountDeactivated: React.FC = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear username error when user starts typing
    if (name === 'username') {
      setUsernameError('');
    }
  };

  const checkUsername = async (username: string) => {
    if (!username.trim()) {
      setUsernameError('');
      return;
    }

    setCheckingUsername(true);
    try {
      const response = await axios.post(
        `${getApiUrl()}/api/support/check-username`,
        { username: username.trim() }
      );
      
      if (!response.data.exists) {
        setUsernameError("This username doesn't exist in our records. Please check and enter a registered one.");
      } else {
        setUsernameError('');
      }
    } catch (error) {
      console.error('Error checking username:', error);
      setUsernameError('Error checking username. Please try again.');
    } finally {
      setCheckingUsername(false);
    }
  };



  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Check for username error before submitting
    if (usernameError) {
      setError('Please fix the username error before submitting.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${getApiUrl()}/api/support/deactivated-inquiry`,
        {
          ...formData,
          inquiryType: 'deactivated_account' // Always set to deactivated_account
        }
      );

      if (response.status === 201) {
        setSubmitted(true);
        setFormData({
          username: '',
          email: '',
          message: '',
        });
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.username && formData.email && formData.message && !usernameError;

  return (
    <>
      <MetaTags 
        title="Account Deactivated - LeadForge"
        description="Your LeadForge account has been deactivated. Contact our support team for assistance with account reactivation."
        keywords="account deactivated, support, reactivation, LeadForge, account access"
      />
      <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}20 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(18, 18, 18, 0.9)' 
              : 'rgba(255, 255, 255, 0.9)',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          {!submitted ? (
            <>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <WarningIcon 
                  sx={{ 
                    fontSize: 64, 
                    color: theme.palette.warning.main,
                    mb: 2 
                  }} 
                />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Account Deactivated
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>
                  Your account has been deactivated by an administrator. Please use the form below to reach out to us for assistance.
                </Typography>
                <Typography variant="body2" color="primary.main" sx={{ maxWidth: 600, mx: 'auto', fontWeight: 500 }}>
                  💡 Please enter your username so we can identify your account and better assist you.
                </Typography>
              </Box>

              {/* Contact Form */}
              <Box component="form" onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Username and Email */}
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      name="username"
                      label="Username"
                      value={formData.username}
                      onChange={handleInputChange}
                      onBlur={(e) => checkUsername(e.target.value)}
                      required
                      fullWidth
                      variant="outlined"
                      error={!!usernameError}
                      helperText={usernameError || 'Enter your account username'}
                      InputProps={{
                        endAdornment: checkingUsername ? (
                          <CircularProgress size={20} />
                        ) : null,
                      }}
                    />
                    <TextField
                      name="email"
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      fullWidth
                      variant="outlined"
                      helperText="You can use any email address for contact"
                    />
                  </Box>



                  {/* Message */}
                  <TextField
                    name="message"
                    label="Message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder="Please describe your issue or question in detail..."
                  />

                  {/* Error Message */}
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!isFormValid || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    sx={{
                      mt: 2,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {loading ? 'Sending...' : 'Send Inquiry'}
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            /* Success Message */
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.success.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <SendIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Inquiry Submitted Successfully
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                We will contact you back soon at the email address you provided.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setSubmitted(false)}
                sx={{ textTransform: 'none' }}
              >
                Submit Another Inquiry
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
    </>
  );
};

export default AccountDeactivated; 